import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';

const EMPTY_FORM = {
  name: '',
  contact_name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  notes: '',
};

export default function ShopManagementPage() {
  const { can } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadShops = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getShops();
      setShops(Array.isArray(data) ? data : (data.shops || []));
    } catch (err) {
      setError(err.message || 'Failed to load shops');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadShops(); }, [loadShops]);

  function openAdd() {
    setEditingShop(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  }

  function openEdit(shop) {
    setEditingShop(shop);
    setForm({
      name: shop.name || '',
      contact_name: shop.contact_name || '',
      phone: shop.phone || '',
      email: shop.email || '',
      address: shop.address || '',
      city: shop.city || '',
      state: shop.state || '',
      zip: shop.zip || '',
      notes: shop.notes || '',
    });
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingShop) {
        await api.updateShop(editingShop.id, form);
      } else {
        await api.createShop(form);
      }
      setShowModal(false);
      loadShops();
    } catch (err) {
      setError(err.message || 'Failed to save shop');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteShop(id);
      setDeleteConfirm(null);
      loadShops();
    } catch (err) {
      setError(err.message || 'Failed to delete shop');
      setDeleteConfirm(null);
    }
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  if (!can('manage_shops')) {
    return <div className="page-content"><p>You do not have permission to manage shops.</p></div>;
  }

  return (
    <div className="shop-table">
      <div className="maint-types__header">
        <h1>Shop Management</h1>
        <button className="btn btn-gold" onClick={openAdd}>+ Add Shop</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading shops...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Zip</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shops.length === 0 ? (
                <tr><td colSpan="9" className="text-center">No shops found. Add one to get started.</td></tr>
              ) : shops.map(shop => (
                <tr key={shop.id}>
                  <td><strong>{shop.name}</strong></td>
                  <td>{shop.contact_name || '—'}</td>
                  <td>{shop.phone || '—'}</td>
                  <td>{shop.email || '—'}</td>
                  <td>{shop.address || '—'}</td>
                  <td>{shop.city || '—'}</td>
                  <td>{shop.state || '—'}</td>
                  <td>{shop.zip || '—'}</td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(shop)}>Edit</button>
                    {' '}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(shop)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editingShop ? 'Edit Shop' : 'Add Shop'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <div className="form-group">
                <label>Shop Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <label>Contact Name</label>
                <input name="contact_name" value={form.contact_name} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input name="address" value={form.address} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>City</label>
                <input name="city" value={form.city} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>State</label>
                <input name="state" value={form.state} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Zip Code</label>
                <input name="zip" value={form.zip} onChange={handleChange} className="form-input" />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} className="form-input" rows={3} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editingShop ? 'Update Shop' : 'Add Shop')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal title="Delete Shop" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
